import { useEffect, useRef, useState, useCallback } from "react";
import { getToken } from "../lib/api";

/**
 * Subscribes to /api/ws/lobby/{code} and exposes:
 *   { state, status, send, lastEvent }
 *
 * Auto-reconnects with exponential backoff (capped at 10s) on transient drops.
 * `status` ∈ "connecting" | "open" | "reconnecting" | "closed"
 * Server pings are answered with pong automatically.
 */
export function useLobbySocket(code, onEvent) {
  const [status, setStatus] = useState("connecting");
  const wsRef = useRef(null);
  const onEventRef = useRef(onEvent);
  const backoffRef = useRef(500);
  const closedByUserRef = useRef(false);
  const reconnectTimerRef = useRef(null);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const connect = useCallback(() => {
    const token = getToken();
    if (!token || !code) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl =
      backendUrl.replace(/^http/, "ws") +
      `/api/ws/lobby/${encodeURIComponent(code)}?token=${encodeURIComponent(token)}`;
    setStatus((s) => (s === "open" ? "reconnecting" : "connecting"));
    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch {
      setStatus("closed");
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      backoffRef.current = 500;
      setStatus("open");
    };
    ws.onmessage = (msg) => {
      try {
        const evt = JSON.parse(msg.data);
        if (evt.type === "ping") {
          ws.send(JSON.stringify({ type: "pong" }));
          return;
        }
        if (onEventRef.current) onEventRef.current(evt);
      } catch {
        // ignore malformed
      }
    };
    ws.onerror = () => {
      // close handler will trigger reconnect
    };
    ws.onclose = (ev) => {
      if (closedByUserRef.current) {
        setStatus("closed");
        return;
      }
      // 4001/4008 = policy violation, don't keep retrying forever for bad token / not-a-member.
      if (ev.code === 1008 || ev.code === 4008) {
        setStatus("closed");
        return;
      }
      setStatus("reconnecting");
      const delay = Math.min(10000, backoffRef.current);
      backoffRef.current = Math.min(10000, backoffRef.current * 2);
      reconnectTimerRef.current = setTimeout(connect, delay);
    };
  }, [code]);

  useEffect(() => {
    closedByUserRef.current = false;
    connect();
    return () => {
      closedByUserRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          // ignore
        }
      }
      wsRef.current = null;
    };
  }, [connect]);

  const send = useCallback((type, payload = {}) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify({ type, payload }));
    return true;
  }, []);

  return { status, send };
}
